import * as React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { alpha } from "@mui/material/styles";
import DialogContext from "../../contexts/DialogContext";
import { useIntegrations } from "../../hooks/query/useIntegrations";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";
import { COLORS } from "../../theme/theme-options";
import type { Integration } from "../../types";

const badgeColorByProvider: Record<string, string> = {
  "system-datadog": "#7c3aed",
  "system-splunk": "#059669",
  "system-solarwinds": "#c2410c",
  "system-sentinel": "#2563eb",
  "system-pagerduty": "#15803d",
};

const TOOL_INTEGRATION_SKELETON_COUNT = 4;

const providerInitials = (name: string): string => {
  const clean = name.trim();
  if (clean.length === 0) {
    return "??";
  }

  const words = clean.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const providerNameFromId = (providerId: string): string => {
  const normalized = providerId.trim();
  if (!normalized) {
    return "Custom";
  }

  const noPrefix = normalized.startsWith("system-")
    ? normalized.slice("system-".length)
    : normalized;

  return noPrefix
    .split(/[-_]+/)
    .filter((chunk) => chunk.length > 0)
    .map((chunk) => {
      const lower = chunk.toLowerCase();
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join(" ");
};

const isUuidLike = (value: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
};

const statusLabel = (ready: boolean): string => {
  if (ready) {
    return "Connected";
  }
  return "Disconnected";
};

const statusColor = (ready: boolean): string => {
  if (ready) {
    return "#34d399";
  }
  return alpha(COLORS.grey[400], 0.8);
};

const lastSyncLabel = (ready: boolean): string => {
  if (ready) {
    return "Recently";
  }
  return "Never";
};

const TruncatedText: React.FC<{
  value: string;
  maxChars?: number;
  sx?: Record<string, unknown>;
}> = ({ value, maxChars = 22, sx }) => {
  const trimmedValue = value.trim();
  const showTooltip = trimmedValue.length > maxChars;

  return (
    <Tooltip title={showTooltip ? trimmedValue : ""} placement="top" arrow>
      <Typography
        sx={{
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...sx,
        }}
      >
        {trimmedValue}
      </Typography>
    </Tooltip>
  );
};

const ToolIntegrationCardSkeleton: React.FC = () => {
  return (
    <Card variant="glass" sx={{ p: 2.2, flex: 1, display: "flex" }}>
      <Stack spacing={1.8} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.4} sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="rounded" width={54} height={54} sx={{ borderRadius: 2.2 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Skeleton variant="rounded" width="68%" height={26} />
              <Skeleton variant="rounded" width="42%" height={18} sx={{ mt: 0.5 }} />
            </Box>
          </Stack>
          <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 999 }} />
        </Stack>

        <Stack spacing={0.8}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rounded" width={72} height={18} />
            <Skeleton variant="rounded" width={78} height={18} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rounded" width={56} height={18} />
            <Skeleton variant="rounded" width={108} height={18} />
          </Stack>
        </Stack>

        <Box sx={{ borderTop: `1px solid ${alpha(COLORS.grey[700], 0.45)}`, pt: 1.4 }}>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Skeleton variant="rounded" width={92} height={32} />
            <Skeleton variant="rounded" width={116} height={32} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

const ToolIntegrationsConfigPage: React.FC = () => {
  const { data: integrations = [], isLoading } = useIntegrations();
  const { data: alertProviders = [] } = useAlertProviders();
  const { show } = React.useContext(DialogContext);

  const providerById = React.useMemo(() => {
    return new Map(alertProviders.map((provider) => [provider.id, provider.name]));
  }, [alertProviders]);

  const renderCard = (integration: Integration) => {
    const ready = integration.auth?.ready === true;
    const isSystemIntegration = integration.system;
    const providerId = (integration.alertSchemaId ?? "").trim();
    const integrationName = (integration.name ?? "").trim();
    const mappedProviderName = providerById.get(providerId)?.trim() ?? "";
    const hasUsableMappedProviderName =
      mappedProviderName.length > 0 &&
      !isUuidLike(mappedProviderName) &&
      mappedProviderName.toLowerCase() !== "unknown provider";
    const providerName = hasUsableMappedProviderName
      ? mappedProviderName
      : isUuidLike(providerId)
        ? ""
        : providerNameFromId(providerId);
    const integrationNameIsIdentifier =
      isUuidLike(integrationName) ||
      integrationName === integration.id ||
      integrationName === providerId;
    const providerNameIsIdentifier =
      providerName.length === 0 ||
      isUuidLike(providerName) ||
      providerName === integration.id ||
      providerName === providerId;
    const fallbackName = isSystemIntegration ? "System integration" : "Custom integration";
    const displayName = integrationNameIsIdentifier
      ? providerNameIsIdentifier
        ? fallbackName
        : providerName
      : integrationName.length > 0
        ? integrationName
        : fallbackName;
    const showProviderSubtitle =
      !providerNameIsIdentifier &&
      providerName.toLowerCase() !== displayName.toLowerCase();
    const initials = providerInitials(displayName);
    const baseColor = badgeColorByProvider[providerId] ?? COLORS.primary[500];

    return (
      <Card key={integration.id} variant="glass" sx={{ p: 2.2 }}>
        <Stack spacing={1.8}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.4} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 2.2,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 22,
                  fontWeight: 700,
                  color: alpha(COLORS.grey[100], 0.95),
                  border: `1px solid ${alpha(baseColor, 0.45)}`,
                  background: `linear-gradient(135deg, ${alpha(baseColor, 0.26)}, ${alpha(baseColor, 0.18)})`,
                }}
              >
                {initials}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <TruncatedText
                  value={displayName}
                  maxChars={18}
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: COLORS.grey[100],
                  }}
                />
                {showProviderSubtitle && (
                  <TruncatedText
                    value={providerName}
                    maxChars={20}
                    sx={{
                      color: COLORS.grey[500],
                      mt: 0.25,
                    }}
                  />
                )}
              </Box>
            </Stack>

            <Chip
              size="small"
              label={ready ? "Active" : "Inactive"}
              sx={{
                color: ready ? "#34d399" : COLORS.grey[500],
                bgcolor: ready ? alpha("#10b981", 0.12) : alpha(COLORS.grey[700], 0.3),
                border: `1px solid ${ready ? alpha("#34d399", 0.3) : alpha(COLORS.grey[500], 0.3)}`,
              }}
            />
          </Stack>

          <Stack spacing={0.8}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ color: COLORS.grey[500] }}>Last Sync</Typography>
              <Typography sx={{ color: COLORS.grey[200], fontWeight: 700 }}>
                {lastSyncLabel(ready)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ color: COLORS.grey[500] }}>Status</Typography>
              <Stack direction="row" spacing={0.7} alignItems="center">
                {ready && (
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: statusColor(ready) }} />
                )}
                {ready === false && (
                  <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 15, color: statusColor(ready) }} />
                )}
                <Typography sx={{ color: statusColor(ready), fontWeight: 700 }}>
                  {statusLabel(ready)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ borderTop: `1px solid ${alpha(COLORS.grey[700], 0.45)}`, pt: 1.4 }}>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <Button
                variant="text"
                startIcon={<SyncRoundedIcon fontSize="small" />}
                disabled={!ready}
                onClick={() => {
                  show({
                    dialog: "import-alerts",
                    integrationId: integration.id,
                  });
                }}
                sx={{ minWidth: 0 }}
              >
                Sync now
              </Button>

              <Button
                variant="text"
                startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                onClick={() => {
                  show({
                    dialog: "setup-integration",
                    integrationId: integration.id,
                  });
                }}
                sx={{ minWidth: 0 }}
              >
                {ready ? "Configure" : "Start setup"}
              </Button>

              <Box sx={{ flex: 1 }} />

              {isSystemIntegration === false && (
                <IconButton
                  size="small"
                  onClick={() => {
                    show({
                      dialog: "confirm-delete",
                      entity: "integration",
                      ids: integration.id,
                    });
                  }}
                  sx={{ color: "#ef4444" }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Box>
        </Stack>
      </Card>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            Tool Integrations
          </Typography>
          <Typography sx={{ color: COLORS.grey[500], mt: 0.3 }}>
            Connect monitoring and observability platforms
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="large"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            show({ dialog: "new-integration" });
          }}
        >
          New
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {isLoading
          ? Array.from({ length: TOOL_INTEGRATION_SKELETON_COUNT }).map((_, index) => (
              <Grid key={`tool-integration-skeleton-${index}`} size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                <ToolIntegrationCardSkeleton />
              </Grid>
            ))
          : integrations.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Card variant="glass" sx={{ p: 3 }}>
              <Stack spacing={0.8} alignItems="flex-start">
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.grey[100] }}>
                  No integrations configured
                </Typography>
                <Typography sx={{ color: COLORS.grey[500] }}>
                  Add your first integration to start collecting alerts.
                </Typography>
              </Stack>
            </Card>
          </Grid>
        )}
        {!isLoading && integrations.map((integration) => (
          <Grid key={integration.id} size={{ xs: 12, md: 6 }}>
            {renderCard(integration)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ToolIntegrationsConfigPage;
